import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import Cookie from 'js-cookie';
import { useQueryClient } from '@tanstack/react-query';
import { ValidationError } from 'utils';
import fetchData from 'utils/fetchData';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';
import { useTranslation } from 'react-i18next';
import useEventCollectorValue from 'hooks/context/EventCollector/useEventCollectorValue';

type LoginResponse = {
  authorizationToken: string;
  fromTemporaryPassword: boolean;
};

type Props = {
  children: ReactNode;
};

type AuthContextType = {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  crossLoginRedirect: () => void;
  crossLoginAuth: (token: string) => Promise<LoginResponse>;
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const useLegacyLoginAuth = (): AuthContextType => useContext(AuthContext);

export function LEGACY_LOGIN_AuthProvider({ children }: Props): JSX.Element {
  const { t } = useTranslation();
  const { showError } = useError();
  const { error } = useSmartlook();
  const queryClient = useQueryClient();
  const { reset } = useEventCollectorValue();

  const login = useCallback(
    async (email: string, password: string) => {
      try {
        const encodedCredentials = window.btoa(`${email}:${password}`);
        const url = import.meta.env.VITE_LEGACY_LOGIN_API;

        const res = await fetch(`${url}/token/basic`, {
          method: 'post',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Basic ${encodedCredentials}`,
          },
        });

        if (res.status === 401) {
          throw new ValidationError([
            {
              message: 'Invalid credentials',
              messageTemplate: 'invalid_credentials',
              property: 'password',
            },
          ]);
        }

        if (!res.ok) {
          throw new Error(res.status.toString());
        }

        const data = await res.json();

        const authorizationToken = window.btoa(
          `${data.username}:${data.token}`,
        );
        const crossLoginToken = window.btoa(
          `${data.username}:${data.token}:${email}`,
        );

        sessionStorage.setItem('token', authorizationToken);
        sessionStorage.setItem('crossLoginToken', crossLoginToken);
      } catch (e) {
        error('[AuthProvider][login]', e);

        if (e instanceof ValidationError) {
          showError({
            message: t('error:unauthorized'),
            type: 'notification',
          });

          throw e;
        }
      }
    },
    [error, showError, t],
  );

  const logout = useCallback(async () => {
    try {
      if (sessionStorage.getItem('token')) {
        await fetchData('/token/invalidate', {
          method: 'delete',
        });

        Cookie.remove('token');
      }
    } catch {
      /* empty */
    }

    reset();
    sessionStorage.clear();
    queryClient.invalidateQueries();
  }, [queryClient, reset]);

  /** Log in user in dashboard app */
  const crossLoginRedirect = useCallback(async () => {
    try {
      const response = await fetchData('/client/security/one-time-token', {
        method: 'post',
      });

      if (!response) {
        throw new Error('No response received from server');
      }

      const { oneTimeToken } = response;

      if (!oneTimeToken) {
        throw new Error('Invalid response: missing oneTimeToken');
      }

      const queryString = window.location.search
        ? `&${window.location.search.substring(1)}`
        : '';

      const redirectURL = `${
        import.meta.env.VITE_DASHBOARD_URL
      }/one-time-token-login?token=${oneTimeToken}${queryString}`;

      sessionStorage.clear();

      window.location.assign(redirectURL);
    } catch (e) {
      error('[AuthProvider][crossLoginRedirect]', e);
    }
  }, [error]);

  const crossLoginAuth = useCallback(
    async (oneTimeToken: string) => {
      try {
        const response = await fetchData('/token/one-time', {
          method: 'post',
          body: JSON.stringify({
            oneTimeToken,
          }),
        });

        if (!response) {
          throw new Error('No response received from server');
        }

        const { username, token, fromTemporaryPassword } = response;

        if (!username || !token) {
          throw new Error('Invalid response: missing username or token');
        }

        const authorizationToken = btoa(`${username}:${token}`);

        sessionStorage.setItem('token', authorizationToken);
        sessionStorage.setItem(
          'fromTemporaryPassword',
          String(fromTemporaryPassword),
        );

        return {
          authorizationToken,
          fromTemporaryPassword,
        };
      } catch (e) {
        error('[AuthProvider][crossLoginAuth]', e);
        throw e;
      }
    },
    [error],
  );

  const profileContextValue = useMemo(
    () => ({
      login,
      logout,
      crossLoginRedirect,
      crossLoginAuth,
    }),
    [login, logout, crossLoginRedirect, crossLoginAuth],
  );

  return (
    <AuthContext.Provider value={profileContextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export default useLegacyLoginAuth;
